import Button, { BUTTON_COLOR, BUTTON_VARIANT } from '@ab-core/button';
import { ROUTES } from '@ab-core/functions';
import type { MutationUpsertShoppingListLineItemArgs } from '@ab-core/graphql/dist';
import { Container, Row } from '@ab-core/grid';
import { useUpsertShoppingListLineItem } from '@ab-core/hooks-v2';
import AddLineItem from '@ab-core/hooks/cartNew/LineItems/addToCart/addToCart';
import { LINK_VARIANT, Link } from '@ab-core/link';
import { Loader } from '@ab-core/loader';
import useText from '@ab-core/localization/src/useText';
import type { ModalType } from '@ab-core/modal';
import Modal from '@ab-core/modal';
import Spacer from '@ab-core/spacer';
import { productStore } from '@ab-core/store';
import { getTestIdProp } from '@ab-core/testing';
import Text, { FontTypes } from '@ab-core/text';
import { toast } from '@ab-core/toast';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import type { CartListModalTabsProps } from './components/tabs';
import { CartListModalTabs } from './components/tabs';
import { SubmitCol } from './index.styled';

const PLACEHOLDER_SPLITT = '$placeholder';

type AddToListModalProps = Pick<ModalType, 'open' | 'onClose'> & {
    primaryButtonClicked?: () => void;
};

export const AddToListsOrCartsModal: FC<AddToListModalProps> = (props) => {
    const { open, onClose, primaryButtonClicked } = props;
    const lineItemProp = productStore.getters.useCartOrShoppingListModal();
    const productAddedToListModalHeaderText = useText('shoppingList.productAddedToListModalHeader');
    const productAddedToListNotification = useText('shoppingList.productAddedToListNotification').split(
        PLACEHOLDER_SPLITT
    );
    const productAddedToCartNotification = useText('cartList.productAddedToCartNotification').split(PLACEHOLDER_SPLITT);

    const [cartIds, setCartIds] = useState<Array<string>>([]);
    const [listIds, setListIds] = useState<Array<string>>([]);

    const isUnqualifiedContractProduct = Array.isArray(lineItemProp)
        ? lineItemProp[0].isUnqualifiedContractProduct
        : lineItemProp?.isUnqualifiedContractProduct || false;

    const {
        addLineItem,
        success: cartSuccess,
        error: cartError,
        pending: cartPending,
        response: updatedData
    } = AddLineItem(cartIds);

    const {
        upsertShoppingListLineItem,
        response: shoppingListResponse,
        loading: shoppingListLoading
    } = useUpsertShoppingListLineItem();

    useEffect(() => {
        if (shoppingListResponse) {
            shoppingListResponse.upsertShoppingListLineItem?.forEach((item) =>
                toast.success(
                    'Artikel hinzugefügt',
                    <Text type={FontTypes.Subtitle} color="gray70">
                        {`${productAddedToListNotification[0]} `}
                        <Link variant={LINK_VARIANT.LINK_ONLY} to={`/liste?name=${item.slug}&id=${item.id}`}>
                            &quot;{item.name}&quot;
                        </Link>{' '}
                        {productAddedToListNotification[1]}
                    </Text>
                )
            );

            if (primaryButtonClicked) {
                primaryButtonClicked();
            }

            onClose();
        }
    }, [shoppingListResponse]);

    useEffect(() => {
        if (cartSuccess) {
            updatedData?.upsertLineItemToCarts?.forEach((item) =>
                toast.success(
                    'Artikel hinzugefügt',
                    <Text type={FontTypes.Subtitle} color="gray70">
                        {productAddedToCartNotification[0]}
                        <Link variant={LINK_VARIANT.LINK_ONLY} to={`${ROUTES.CART}?${item.id}`}>
                            <>
                                {
                                    item?.custom?.customFieldsRaw?.find(
                                        (customFieldRaw) => customFieldRaw.name === 'cart-name'
                                    )?.value
                                }
                            </>
                        </Link>
                        {productAddedToCartNotification[1]}
                    </Text>
                )
            );

            if (primaryButtonClicked) {
                primaryButtonClicked();
            }

            onClose();
        }
    }, [cartSuccess]);

    useEffect(() => {
        if (cartError) {
            toast.error('Eingabe konnte nicht gespeichert werden');
        }
    }, [cartError]);

    const handleUpdateCarts: CartListModalTabsProps['onCheckCarts'] = (updatedCartIds) => {
        setCartIds(updatedCartIds);
    };
    const handleUpdateLists: CartListModalTabsProps['onCheckLists'] = (updatedListIds) => {
        setListIds(updatedListIds);
    };

    const createArrayOfLineItemInput = () => {
        if (Array.isArray(lineItemProp)) {
            return lineItemProp.map((item) => ({ quantity: item.quantity, sku: item.sku, id: undefined, fields: [] }));
        }

        return [{ quantity: lineItemProp?.quantity, sku: lineItemProp?.sku, id: undefined, fields: [] }];
    };

    const createArrayOfLineItemsForShoppingList = (): MutationUpsertShoppingListLineItemArgs['lineItemInput'] => {
        if (Array.isArray(lineItemProp)) {
            return lineItemProp.map((item) => ({ quantity: item.quantity, sku: item.sku }));
        }

        return [{ quantity: lineItemProp?.quantity || 1, sku: lineItemProp?.sku || '' }];
    };

    const handleSave = () => {
        if (cartIds.length > 0) {
            addLineItem({
                variables: {
                    lineItemInput: createArrayOfLineItemInput()
                }
            });
        } else {
            if (lineItemProp) {
                upsertShoppingListLineItem({
                    ids: listIds,
                    lineItemInput: createArrayOfLineItemsForShoppingList()
                });
            }
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            headerText={productAddedToListModalHeaderText}
            md={8}
            lg={7}
            xl={5}
            footer={
                <Container>
                    <Row justifyContent="between">
                        <SubmitCol sm={6} smOrder={2}>
                            <Button
                                {...getTestIdProp('save-add-to-list-or-carts-modal-button')}
                                disabled={
                                    !!(cartIds.length === 0 && listIds.length === 0) ||
                                    shoppingListLoading ||
                                    cartPending
                                }
                                onClick={handleSave}
                                fluid
                            >
                                Speichern
                            </Button>
                        </SubmitCol>

                        <SubmitCol sm={6} smOrder={1}>
                            <Button color={BUTTON_COLOR.GRAY} fluid variant={BUTTON_VARIANT.OUTLINE} onClick={onClose}>
                                Abbrechen
                            </Button>
                        </SubmitCol>
                    </Row>
                </Container>
            }
        >
            {(shoppingListLoading || cartPending) && <Loader />}
            <Container>
                <Spacer size="xs" />
                <CartListModalTabs
                    isUnqualifiedContractProduct={!!isUnqualifiedContractProduct}
                    onCheckCarts={handleUpdateCarts}
                    onCheckLists={handleUpdateLists}
                    onClose={onClose}
                />
            </Container>
        </Modal>
    );
};
